import React, { useState } from 'react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';


const cohorts = [
  { month: 'Jan', w1: 82, w2: 68, w4: 54, w8: 41, w12: 33 },
  { month: 'Feb', w1: 79, w2: 65, w4: 51, w8: 38, w12: 30 },
  { month: 'Mar', w1: 85, w2: 72, w4: 58, w8: 45, w12: 36 },
  { month: 'Apr', w1: 88, w2: 74, w4: 61, w8: 47, w12: null },
  { month: 'May', w1: 84, w2: 70, w4: 56, w8: null, w12: null },
  { month: 'Jun', w1: 91, w2: 77, w4: null, w8: null, w12: null },
];

const heatData = [
  { cohort: 'Jan 2026', w0: 100, w1: 82, w2: 68, w4: 54, w8: 41, w12: 33 },
  { cohort: 'Feb 2026', w0: 100, w1: 79, w2: 65, w4: 51, w8: 38, w12: 30 },
  { cohort: 'Mar 2026', w0: 100, w1: 85, w2: 72, w4: 58, w8: 45, w12: 36 },
  { cohort: 'Apr 2026', w0: 100, w1: 88, w2: 74, w4: 61, w8: 47, w12: null },
  { cohort: 'May 2026', w0: 100, w1: 84, w2: 70, w4: 56, w8: null, w12: null },
  { cohort: 'Jun 2026', w0: 100, w1: 91, w2: 77, w4: null, w8: null, w12: null },
];

const weeks = ['Week 0', 'Week 1', 'Week 2', 'Week 4', 'Week 8', 'Week 12'];
const keys = ['w0', 'w1', 'w2', 'w4', 'w8', 'w12'];

const getColor = (val) => {
  if (val === null) return 'bg-muted/30 text-muted-foreground/40';
  if (val >= 80) return 'bg-primary text-white';
  if (val >= 60) return 'bg-primary/70 text-white';
  if (val >= 40) return 'bg-primary/40 text-foreground';
  if (val >= 20) return 'bg-primary/20 text-foreground';
  return 'bg-primary/10 text-muted-foreground';
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload?.length) {
    return (
      <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs">
        <p className="font-semibold text-foreground mb-1">{label}</p>
        {payload?.map(p => (
          <p key={p?.dataKey} style={{ color: p?.color }}>
            {p?.name}: {p?.value}%
          </p>
        ))}
      </div>
    );
  }
  return null;
};

const CohortRetention = () => {
  const [view, setView] = useState('heatmap');

  return (
    <div className="bg-card border border-border rounded-xl p-5">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h3 className="text-sm font-semibold text-foreground">Cohort Retention</h3>
          <p className="text-xs text-muted-foreground mt-0.5">User retention by signup cohort</p>
        </div>
        <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
          {['heatmap', 'chart']?.map(v => (
            <button
              key={v}
              onClick={() => setView(v)}
              className={`px-3 py-1 rounded-md text-xs font-medium transition-all ${
                view === v ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              {v === 'heatmap' ? 'Heatmap' : 'Trend'}
            </button>
          ))}
        </div>
      </div>
      {view === 'heatmap' ? (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr>
                <th className="text-left text-muted-foreground font-medium pb-2 pr-3 whitespace-nowrap">Cohort</th>
                {weeks?.map(w => (
                  <th key={w} className="text-center text-muted-foreground font-medium pb-2 px-1 whitespace-nowrap">{w}</th>
                ))}
              </tr>
            </thead>
            <tbody className="space-y-1">
              {heatData?.map(row => (
                <tr key={row?.cohort}>
                  <td className="text-muted-foreground pr-3 py-1 whitespace-nowrap">{row?.cohort}</td>
                  {keys?.map(k => (
                    <td key={k} className="px-1 py-1 text-center">
                      <div className={`rounded-md px-2 py-1.5 font-semibold ${getColor(row?.[k])}`}>
                        {row?.[k] !== null ? `${row?.[k]}%` : '—'}
                      </div>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={220}>
          <AreaChart data={cohorts} margin={{ top: 4, right: 4, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis dataKey="month" tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} />
            <YAxis tick={{ fontSize: 11, fill: 'var(--color-muted-foreground)' }} unit="%" />
            <Tooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Area type="monotone" dataKey="w1" name="Week 1" stroke="#107570" fill="#107570" fillOpacity={0.15} strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="w2" name="Week 2" stroke="#0d9488" fill="#0d9488" fillOpacity={0.12} strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="w4" name="Week 4" stroke="#14b8a6" fill="#14b8a6" fillOpacity={0.1} strokeWidth={2} dot={false} />
            <Area type="monotone" dataKey="w8" name="Week 8" stroke="#2dd4bf" fill="#2dd4bf" fillOpacity={0.08} strokeWidth={2} dot={false} />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default CohortRetention;
